<template>
    <v-card v-if="stats && columns.length" elevation="2" :color="isAja ? '#1E3945' : ''" :dark="isAja">
        <v-card-text class="py-3">
            <div class="text-subtitle-1 font-weight-bold mb-1 px-3" :class="titleClass">Mon parcours</div>
            <v-row no-gutters>
                <template v-for="(col, idx) in columns">
                    <v-divider v-if="idx > 0" :key="'divider-' + col.key" :vertical="$vuetify.breakpoint.smAndUp" />
                    <v-col :key="col.key" cols="12" :sm="columns.length === 1 ? 12 : 6" class="px-3 py-1">
                        <div class="text-overline font-weight-bold" :class="titleClass">{{ col.label }}</div>

                        <div class="d-flex align-baseline mb-1">
                            <span class="text-h5 font-weight-bold" :class="quotaColorClass(col.remaining)">
                                {{ col.remaining }}
                            </span>
                            <span class="ml-2 text-body-2" :class="hintColorClass">restante(s) / {{ col.limit }}</span>
                        </div>
                        <v-progress-linear
                            :value="(col.consumed / Math.max(col.limit, 1)) * 100"
                            :color="quotaBarColor(col.remaining)"
                            height="6"
                            rounded
                            class="mb-2"
                        ></v-progress-linear>

                        <template v-for="(row, rowIdx) in col.rows">
                            <v-divider
                                v-if="row.divider"
                                :key="'rdiv-' + col.key + '-' + rowIdx"
                                class="my-1"
                            />
                            <div v-else :key="col.key + '-' + row.label" class="d-flex align-center px-2 recap-row">
                                <span class="text-body-2" :class="labelColorClass">{{ row.label }}</span>
                                <v-chip v-if="row.chip" x-small label outlined :color="row.chipColor" class="ml-2">{{ row.chip }}</v-chip>
                                <v-spacer />
                                <span class="text-body-2 font-weight-bold" :class="row.colorClass || valueColorClass">{{ row.value }}</span>
                            </div>
                        </template>
                    </v-col>
                </template>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<script>
export default {
    name: 'MeetingsRecap',
    data() {
        return {
            stats: null,
        }
    },
    computed: {
        profile() {
            return this.$store.getters.getProfile
        },
        isAja() {
            return this.profile?.is_aja
        },
        ajaRole() {
            return this.profile?.aja_role
        },
        titleClass() {
            return this.isAja ? 'aja-title' : 'primary--text'
        },
        labelColorClass() {
            return this.isAja ? 'white--text' : 'text--primary'
        },
        valueColorClass() {
            return this.isAja ? 'white--text' : 'text--primary'
        },
        hintColorClass() {
            return this.isAja ? 'grey--text text--lighten-1' : 'text--secondary'
        },
        columns() {
            if (!this.stats) return []
            if (this.isAja && this.ajaRole === 'aja_young') {
                return [
                    this.cogColumn(this.stats.aja_cognitive, 'Cognitive'),
                ].filter(Boolean)
            }
            if (this.isAja && this.ajaRole === 'aja_assistant') {
                return [
                    this.oncColumn(this.stats.aja_webin, 'WEBIN\'PAP'),
                ].filter(Boolean)
            }
            if (this.profile?.role === 'pro') {
                return [
                    this.cogColumn(this.stats.pro || this.stats.cognitive, 'PRO'),
                ].filter(Boolean)
            }
            return [
                this.cogColumn(this.stats.cognitive, 'Cognitive'),
            ].filter(Boolean)
        },
    },
    mounted() {
        this.fetchStats()
    },
    methods: {
        cogColumn(block, label) {
            if (!block) return null
            return {
                key: 'cog-' + label,
                label,
                limit: block.limit,
                consumed: block.consumed,
                remaining: block.remaining,
                rows: [
                    { label: 'À venir', value: block.upcoming },
                    { label: 'Suivies', value: block.presences, colorClass: this.successClass() },
                    { label: 'Absences', value: block.absences, colorClass: block.absences > 0 ? this.errorClass() : null },
                    { divider: true },
                    {
                        label: 'Annul. tardives',
                        value: block.late_unsubscriptions,
                        colorClass: block.late_unsubscriptions > 0 ? 'orange--text text--darken-2' : null,
                    },
                    {
                        label: 'Annul. légitimes',
                        value: block.legitimate_unsubscriptions,
                    },
                ],
            }
        },
        oncColumn(block, label) {
            if (!block) return null
            return {
                key: 'onc-' + label,
                label,
                limit: block.limit,
                consumed: block.consumed != null ? block.consumed : block.validated,
                remaining: block.remaining,
                rows: [
                    { label: 'À venir', value: block.upcoming },
                    { label: 'Suivies', value: block.presences, colorClass: this.successClass() },
                    { label: 'Absences', value: block.absences, colorClass: block.absences > 0 ? this.errorClass() : null },
                    { divider: true },
                    {
                        label: 'Annulations',
                        value: block.canceled,
                    },
                ],
            }
        },
        successClass() {
            return this.isAja ? 'aja-success' : 'success--text'
        },
        errorClass() {
            return this.isAja ? 'aja-error' : 'error--text'
        },
        quotaColorClass(remaining) {
            if (this.isAja) return remaining > 0 ? 'aja-success' : 'aja-error'
            return remaining > 0 ? 'success--text' : 'error--text'
        },
        quotaBarColor(remaining) {
            return remaining > 0 ? 'success' : 'error'
        },
        fetchStats() {
            const profileId = this.profile?.id
            if (!profileId) return

            this.$store.dispatch('userRequest', { id: profileId })
                .then(result => {
                    this.stats = result.data.session_stats || null
                })
                .catch(err => {
                    if (err.response?.status !== 403) {
                        this.$store.dispatch('setErrors', err.response?.data)
                    }
                })
        },
    },
}
</script>

<style scoped>
.recap-row {
    min-height: 28px;
    padding-top: 2px;
    padding-bottom: 2px;
}
.aja-title {
    color: #4E983C;
}
.aja-success {
    color: #4E983C;
}
.aja-error {
    color: #ff8a80;
}
</style>
